import {
  fetchCSV,
  fetchVersionFileContent,
  normalizeAllNodes,
  parseCsv,
  uploadFile,
} from '../../utils';
import type { TableRow } from '../table-store';
import { withAiGatewaySettings } from './ai-native-app-py';

/**
 * The AI-Native template's files (`public/templates/ai-native/`), and putting
 * the ones a solution runs into its draft version: `CallAIModel.py` and
 * `CallAIClassifier.py` (the Supervisor's OpenAI Decisions routing) as action
 * scripts, and `app.py` with the settings they read. The row swap itself is
 * `ai-native-swap.ts`.
 */

const TEMPLATE_DIR = '/templates/ai-native';

/** The template's Supervisor and Knowledge Agent rows, without ids. */
export const loadAiNativeTemplate = async (): Promise<TableRow[]> =>
  normalizeAllNodes(
    await parseCsv(await fetchCSV(`${TEMPLATE_DIR}/supervisor-knowledge.csv`))
  );

/**
 * Demo only: the support information `FE_BOBCAT_DEMO_KNOWLEDGE` names (a
 * path the app serves), or null when it's unset or can't be read.
 */
export const loadDemoKnowledge = async (
  path = import.meta.env.FE_BOBCAT_DEMO_KNOWLEDGE
): Promise<string | null> => {
  if (!path?.trim()) return null;
  try {
    const res = await fetch(path.trim());
    return res.ok ? await res.text() : null;
  } catch {
    return null;
  }
};

interface VersionFiles {
  projectId: string;
  /** `customerId.botName.vN` — the draft. */
  versionId: string;
}

/** One upload to bot-manager, as the Actions panel sends it. */
const upload = (
  { projectId, versionId }: VersionFiles,
  { kind, file }: { kind: 'scripts' | 'config'; file: File }
) =>
  new Promise<unknown>((resolve, reject) => {
    uploadFile({
      url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/${kind}`,
      file: [file],
      type: kind === 'scripts' ? 'scriptFile' : 'configFile',
      onChange: ({ data }) => resolve(data),
      error: (err) => reject(err),
    });
  });

/** The action scripts the template's rows run. */
export const AI_NATIVE_SCRIPTS = ['CallAIModel.py', 'CallAIClassifier.py'];

/**
 * Adds the template's scripts and the `app.py` settings to the draft.
 * Resolves with the draft data bot-manager returns after the last upload (for
 * the caller to put in context), or throws.
 */
export const installAiNativeFiles = async (
  version: VersionFiles
): Promise<unknown> => {
  let afterScript: unknown;
  for (const name of AI_NATIVE_SCRIPTS) {
    // eslint-disable-next-line no-await-in-loop -- one upload at a time
    const script = await fetch(`${TEMPLATE_DIR}/${name}`).then((r) => {
      if (!r.ok) throw new Error(`The AI-Native ${name} is missing.`);
      return r.text();
    });
    // eslint-disable-next-line no-await-in-loop -- one upload at a time
    afterScript = await upload(version, {
      kind: 'scripts',
      file: new File([script], name, { type: 'text/x-python' }),
    });
  }
  const appPy = await fetchVersionFileContent({
    projectId: version.projectId,
    versionId: version.versionId,
    filePath: 'app.py',
  });
  const next = withAiGatewaySettings(appPy);
  if (next === appPy) return afterScript;
  return upload(version, {
    kind: 'config',
    file: new File([next], 'app.py', { type: 'text/x-python' }),
  });
};
